import type { MarketFilters, MarketFilterTab } from "@/shared/types/bondFilters";
import { FILTER_TAB_RESTRICTIONS } from "@/shared/types/bondFilters";

/**
 * Unified Markets filter state helpers.
 *
 * The Markets page keeps ONE canonical `MarketFilters` object (persisted to
 * localStorage) that spans every tab. The URL only carries the subset of
 * filters that apply to the active tab (the "visible" state). Filters that do
 * not apply to the active tab are masked out of the URL/results but kept in the
 * canonical object so they are restored when the user revisits a tab where they
 * apply.
 *
 * Tab applicability is driven entirely by `FILTER_TAB_RESTRICTIONS`, so adding
 * a new tab-specific filter is a one-line config change.
 */

/** Sort lives per-tab (columns differ), so it is never carried in the canonical state. */
const NON_CANONICAL_KEYS = new Set<keyof MarketFilters>(["sortBy", "sortDesc", "tab"]);

/** Whether a filter key applies to the given tab. Keys absent from the config apply to all tabs. */
export function filterAppliesToTab(key: keyof MarketFilters, tab: MarketFilterTab): boolean {
  const allowed = FILTER_TAB_RESTRICTIONS[key];
  return allowed == null || allowed.includes(tab);
}

/**
 * Project a filter set down to the keys that apply to `tab` (the visible state).
 * Keys that don't apply to the tab are dropped; `sortBy`/`sortDesc` pass through
 * untouched so a sort applied within a tab survives ordinary filter edits.
 */
export function maskFiltersToTab(filters: MarketFilters, tab: MarketFilterTab): MarketFilters {
  const result: MarketFilters = { tab };
  const rec = result as unknown as Record<string, unknown>;
  for (const key of Object.keys(filters) as (keyof MarketFilters)[]) {
    if (key === "tab") continue;
    if (filterAppliesToTab(key, tab)) {
      const value = filters[key];
      if (value != null) rec[key] = value;
    }
  }
  return result;
}

/**
 * Fold the current tab's visible filters back into the canonical state.
 * Keys applicable to `tab` are taken from `visible` (so edits and clears on the
 * active tab win); keys that don't apply keep their stashed canonical value (so
 * other tabs' filters are preserved). Sort keys are intentionally not persisted.
 */
export function mergeVisibleIntoCanonical(
  canonical: MarketFilters,
  visible: MarketFilters,
  tab: MarketFilterTab,
): MarketFilters {
  const keys = new Set<keyof MarketFilters>([
    ...(Object.keys(canonical) as (keyof MarketFilters)[]),
    ...(Object.keys(visible) as (keyof MarketFilters)[]),
  ]);

  const result: MarketFilters = { tab };
  const rec = result as unknown as Record<string, unknown>;
  for (const key of keys) {
    if (NON_CANONICAL_KEYS.has(key)) continue;
    const value = filterAppliesToTab(key, tab) ? visible[key] : canonical[key];
    if (value != null) rec[key] = value;
  }
  return result;
}
